import * as React from "react";
import { CalendarIcon } from "lucide-react";
import type { Matcher } from "react-day-picker";

import { Button } from "~/shared/ui/button";
import { Calendar } from "~/shared/ui/calendar";
import { Input } from "~/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";

const pad = (n: number) => String(n).padStart(2, "0");

/** Момент, приведённый в границы. Границы включительные, `min` побеждает при пересечении. */
export function clampDateTime(value: Date, min?: Date | null, max?: Date | null): Date {
    if (max && value.getTime() > max.getTime()) value = new Date(max);
    if (min && value.getTime() < min.getTime()) value = new Date(min);
    return value;
}

/** `HH:MM:SS` для `<input type="time" step="1">`. */
export function formatTimeInput(value: Date): string {
    return `${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
}

/** День из `day` + время из строки `HH:MM[:SS]`. Пустая или битая строка оставляет время как было. */
export function applyTimeInput(day: Date, time: string): Date {
    const [h, m, s] = time.split(":");
    const hours = Number(h);
    const minutes = Number(m);
    const seconds = s === undefined ? 0 : Number(s);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds))
        return new Date(day);
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes, seconds);
}

/** Тот же день, что у `value`, но со временем из `source`. */
function withTimeOf(day: Date, source: Date): Date {
    return new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        source.getHours(),
        source.getMinutes(),
        source.getSeconds(),
    );
}

function dayMatchers(min?: Date | null, max?: Date | null): Matcher[] {
    const matchers: Matcher[] = [];
    if (min) matchers.push({ before: new Date(min.getFullYear(), min.getMonth(), min.getDate()) });
    if (max) matchers.push({ after: new Date(max.getFullYear(), max.getMonth(), max.getDate()) });
    return matchers;
}

/**
 * Выбор даты и времени с точностью до секунд.
 *
 * Границы `min`/`max` действуют на момент целиком, а не на день: выбранный день,
 * совпадающий с днём границы, ужимает и время (например, сегодняшняя дата не даёт
 * поставить время из будущего). Дни за границами в календаре недоступны.
 */
export function DateTimePicker({
    id,
    value,
    onChange,
    min,
    max,
    placeholder,
    disabled = false,
}: {
    id?: string;
    value?: Date | null;
    onChange?: (value: Date) => void;
    min?: Date | null;
    max?: Date | null;
    placeholder?: string;
    disabled?: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    const current = value ?? null;

    const emit = (next: Date) => onChange?.(clampDateTime(next, min, max));

    const handleDaySelect = (day: Date | undefined) => {
        if (!day) return;
        emit(withTimeOf(day, current ?? new Date()));
        setOpen(false);
    };

    const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        emit(applyTimeInput(current ?? new Date(), event.target.value));
    };

    return (
        <div className="flex items-center gap-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        id={id}
                        type="button"
                        disabled={disabled}
                        className="flex-1 justify-between font-normal hover:bg-secondary-container hover:text-text-primary"
                    >
                        {current ? current.toLocaleString() : (placeholder ?? "Select date and time")}
                        <CalendarIcon />
                    </Button>
                </PopoverTrigger>
                <PopoverContent disablePortal className="w-auto overflow-hidden p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={current ?? undefined}
                        defaultMonth={current ?? max ?? undefined}
                        captionLayout="dropdown"
                        disabled={dayMatchers(min, max)}
                        onSelect={handleDaySelect}
                        autoFocus
                    />
                </PopoverContent>
            </Popover>
            <Input
                type="time"
                step={1}
                aria-label="time"
                disabled={disabled}
                className="w-[9.5rem]"
                value={current ? formatTimeInput(current) : ""}
                onChange={handleTimeChange}
            />
        </div>
    );
}
